import { useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Crown, Brain, MessageSquare, Scale, FolderOpen, TrendingUp, Shield, Settings } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Modal from './Modal';

interface Service {
  title: string;
  description: string[];
  icon: LucideIcon;
}

interface FormationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const services: Service[] = [
  {
    title: 'Formations en management et leadership',
    icon: Crown,
    description: [
      'Développement du leadership et de la posture managériale.',
      'Management d\'équipe au quotidien : fixer un cadre, motiver, responsabiliser.',
      'Communication managériale et feedback constructif.',
      'Prévention des tensions et gestion des situations délicates.'
    ]
  },
  {
    title: 'Gestion du stress et prévention des risques psychosociaux (RPS)',
    icon: Brain,
    description: [
      'Compréhension des mécanismes du stress au travail.',
      'Identification des signaux d\'alerte individuels et collectifs.',
      'Outils concrets de régulation du stress en situation professionnelle.',
      'Sensibilisation aux RPS et obligations de l\'employeur.'
    ]
  },
  {
    title: 'Communication professionnelle et relations de travail',
    icon: MessageSquare,
    description: [
      'Communication assertive et écoute active.',
      'Gestion des désaccords et des tensions au quotidien.',
      'Travail sur les comportements et les postures professionnelles.',
      'Amélioration de la coopération interservices.'
    ]
  },
  {
    title: 'Sensibilisation au droit du travail pour managers et dirigeants',
    icon: Scale,
    description: [
      'Rôles et responsabilités du manager.',
      'Gestion du temps de travail, absences et discipline.',
      'Bonnes pratiques en matière de sanctions et procédures.',
      'Prévention des risques juridiques et contentieux.'
    ]
  },
  {
    title: 'Formation à la gestion RH pour non-spécialistes',
    icon: FolderOpen,
    description: [
      'Fondamentaux RH : contrats, obligations, documents clés.',
      'Organisation RH simple et efficace.',
      'Lecture et compréhension des bulletins de paie.',
      'Anticipation des risques sociaux.'
    ]
  },
  {
    title: 'Formation à la prise de poste et à l\'évolution professionnelle',
    icon: TrendingUp,
    description: [
      'Intégration et prise de poste réussie.',
      'Organisation du travail et priorisation.',
      'Développement de la confiance professionnelle.',
      'Préparation à une évolution ou une prise de responsabilité.'
    ]
  },
  {
    title: 'Formation à la prévention et gestion des conflits',
    icon: Shield,
    description: [
      'Compréhension des mécanismes du conflit.',
      'Techniques de désamorçage et médiation de premier niveau.',
      'Posture managériale face aux tensions.',
      'Construction de solutions durables.'
    ]
  },
  {
    title: 'Formations sur-mesure intra-entreprise',
    icon: Settings,
    description: [
      'Analyse des besoins et objectifs.',
      'Contenu personnalisé selon le public.',
      'Cas pratiques issus de l\'entreprise.',
      'Évaluation et ajustement post-formation.'
    ]
  }
];

export default function FormationsModal({ isOpen, onClose }: FormationsModalProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleAccordion = useCallback((index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nos Formations"
    >
      <div className="p-6 space-y-4">
        {services.map((service, index) => (
          <div
            key={index}
            className="border-2 border-aurore-light dark:border-warm-gray-700 rounded-xl overflow-hidden hover:border-aurore-dark transition-colors"
          >
            <button
              onClick={() => toggleAccordion(index)}
              className="w-full flex items-center justify-between p-4 bg-white dark:bg-warm-gray-900 hover:bg-aurore-light/30 dark:hover:bg-warm-gray-800 transition-colors text-left"
              aria-expanded={openIndex === index}
            >
              <div className="flex items-center space-x-3 pr-4">
                <service.icon className="w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-gray-100">
                  {service.title}
                </h3>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            <div
              className={`grid transition-all duration-500 ease-in-out ${
                openIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="p-4 bg-aurore-light/20 dark:bg-warm-gray-800/50 space-y-2">
                  {service.description.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <span className="text-aurore-dark font-bold text-lg leading-none mt-1" aria-hidden="true">✓</span>
                      <p className="text-gray-700 dark:text-warm-gray-300 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
